import { LoadingOutlined } from '@ant-design/icons'
import { throttle } from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import styles from './kanaban.module.scss'
import './kanaban.scss'

function getWidthExcludingPadding(element) {
    if (!element) return 0;

    // Get the computed styles of the element
    const style = window.getComputedStyle(element);

    // Extract left and right padding values
    const paddingLeft = parseFloat(style.paddingLeft) || 0;
    const paddingRight = parseFloat(style.paddingRight) || 0;

    // Calculate the scrollWidth excluding padding
    const scrollWidthExcludingPadding = element.clientWidth - paddingLeft - paddingRight;

    return scrollWidthExcludingPadding;
}

const KanabanBoard = ({ columns, isLoading, renderItem, onItemClick, hasMore, loadMore }) => {
    const endElementRef = useRef([])
    const kanabanRef = useRef(null)
    const location = useLocation()
    const observer = useRef(null)
    const scrollMapRef = useRef(null)
    const scrollIndicatorRef = useRef(null)
    const isDragging = useRef(false)
    const startDragX = useRef(0)
    const hasMoreRef = useRef(hasMore)
    const isLoadingRef = useRef(isLoading)
    const loadMoreRef = useRef(loadMore)
    const loadMorePendingRef = useRef(false)

    const visibleColumns = useMemo(() => (
        columns.filter(({ count, items = [] }) => (count ?? items.length) > 0)
    ), [columns])

    useEffect(() => {
        hasMoreRef.current = hasMore
        isLoadingRef.current = isLoading
        loadMoreRef.current = loadMore

        if (!isLoading || !hasMore) {
            loadMorePendingRef.current = false
        }
    }, [hasMore, isLoading, loadMore])

    const debouncedLoadMore = useRef(
        throttle(() => {
            if (!hasMoreRef.current || isLoadingRef.current || loadMorePendingRef.current) return

            loadMorePendingRef.current = true
            const loadMoreResult = loadMoreRef.current?.()

            if (loadMoreResult?.finally) {
                loadMoreResult.finally(() => {
                    if (!isLoadingRef.current) {
                        loadMorePendingRef.current = false
                    }
                })
            }
        }, 200)
    ).current

    useEffect(() => {
        return () => {
            debouncedLoadMore.cancel()
        };
    }, [debouncedLoadMore]);

    const updateScrollIndicator = useCallback(() => {
        const kanaban = kanabanRef.current
        const scrollMap = scrollMapRef.current?.parentElement
        const scrollIndicator = scrollIndicatorRef.current

        if (!kanaban || !scrollMap || !scrollIndicator) return
        const mapRatio = kanaban.clientWidth / kanaban.scrollWidth
        const scrollMapInnerWidth = getWidthExcludingPadding(scrollMap) + 4
        const indicatorWidth = Math.max(20, mapRatio * scrollMapInnerWidth) // Minimum width of 20px
        if (indicatorWidth === scrollMapInnerWidth) {
            scrollMap.style.visibility = 'hidden'
            return
        }
        scrollMap.style.visibility = 'visible'
        scrollIndicator.style.width = `${indicatorWidth}px`

        const leftRatio = kanaban.scrollLeft / (kanaban.scrollWidth - kanaban.clientWidth)
        const indicatorLeft = leftRatio * (scrollMapInnerWidth - indicatorWidth)
        scrollIndicator.style.left = `${indicatorLeft}px`
    }, [])

    const handleScroll = useCallback(() => {
        updateScrollIndicator()
    }, [updateScrollIndicator])

    useEffect(() => {
        endElementRef.current = endElementRef.current.slice(0, visibleColumns.length)
        updateScrollIndicator()
    }, [updateScrollIndicator, visibleColumns])

    useEffect(() => {
        if (observer.current) observer.current.disconnect()
        if (!endElementRef.current.length || !hasMore) return

        observer.current = new IntersectionObserver((entries) => {
            const anyIntersecting = entries.some(entry => entry.isIntersecting)
            if (anyIntersecting) {
                debouncedLoadMore()
            }
        }, {
            root: kanabanRef.current,
            rootMargin: '0px',
            threshold: [0, 0.5, 1],
        })

        endElementRef.current.forEach(el => el && observer.current.observe(el))

        return () => {
            if (observer.current) {
                observer.current.disconnect()
            }
        }
    }, [hasMore, debouncedLoadMore, visibleColumns])

    useEffect(() => {
        kanabanRef.current?.scrollTo(0, 0)
    }, [location])

    const handleDrag = useCallback((e) => {
        if (!kanabanRef.current || !scrollMapRef.current) return

        const deltaX = (e.clientX - startDragX.current) * 1.20 //Increase speed by 20%
        const scrollMap = scrollMapRef.current
        const kanaban = kanabanRef.current

        const { scrollWidth, clientWidth, scrollLeft } = kanaban
        const scrollMapWidth = scrollMap.offsetWidth

        const mapRatio = (scrollWidth - clientWidth) / scrollMapWidth

        const newScrollLeft = scrollLeft + deltaX * mapRatio

        const clampedScrollLeft = Math.max(
            0,
            Math.min(newScrollLeft, scrollWidth - clientWidth)
        )

        kanaban.scrollLeft = clampedScrollLeft

        startDragX.current = e.clientX

        updateScrollIndicator()
    }, [updateScrollIndicator])

    const handleDragEnd = useCallback(() => {
        isDragging.current = false
        document.removeEventListener('mousemove', handleDrag)
        document.removeEventListener('mouseup', handleDragEnd)
        if (scrollIndicatorRef.current) {
            scrollIndicatorRef.current.style.cursor = 'grab'
        }
    }, [handleDrag])

    const handleDragStart = useCallback((e) => {
        e.preventDefault();
        isDragging.current = true
        startDragX.current = e.clientX
        document.addEventListener('mousemove', handleDrag)
        document.addEventListener('mouseup', handleDragEnd)
        if (scrollIndicatorRef.current) {
            scrollIndicatorRef.current.style.cursor = 'grabbing'
        }
    }, [handleDrag, handleDragEnd])

    useEffect(() => {
        const kanaban = kanabanRef.current
        if (kanaban) {
            kanaban.addEventListener('scroll', handleScroll, { passive: true })
            updateScrollIndicator()
        }

        return () => {
            if (kanaban) kanaban.removeEventListener('scroll', handleScroll)
        }
    }, [handleScroll, updateScrollIndicator])

    useEffect(() => {
        const kanaban = kanabanRef.current
        const scrollMap = scrollMapRef.current?.parentElement

        if (!kanaban || !scrollMap) return undefined

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', updateScrollIndicator)
            return () => window.removeEventListener('resize', updateScrollIndicator)
        }

        const resizeObserver = new ResizeObserver(updateScrollIndicator)
        resizeObserver.observe(kanaban)
        resizeObserver.observe(scrollMap)

        return () => {
            resizeObserver.disconnect()
        }
    }, [updateScrollIndicator])

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleDrag)
            document.removeEventListener('mouseup', handleDragEnd)
        }
    }, [handleDrag, handleDragEnd])

    const itemRender = useCallback((data) => {
        try {
            return renderItem(data)
        } catch (error) {
            console.error(error, data)
            return <div className="">Error Rendering Item</div>
        }
    }, [renderItem])

    return (
        <div className="kanaban-wrapper">
            <div className={styles.kanabanWrapper} ref={kanabanRef}>
                <div className={styles.kanaban}>
                    {visibleColumns.map(({ key, title, items = [], count }, index) => (
                        <div
                            key={key}
                            className={(items.length === 0 && !hasMore) ? styles.kanabanColumnVanish : styles.kanabanColumn}
                        >
                            <div className={styles.kanabanColumnHeader} title={title}>
                                <span className={styles.kanabanColumnTitle}>{title}</span>
                                <span className={styles.kanabanColumnCount}>
                                    ({count ?? items.length})
                                </span>
                            </div>
                            {items.map(item => (
                                <React.Fragment key={item.id}>
                                    {itemRender({ item, onClick: onItemClick })}
                                </React.Fragment>
                            ))}
                            <div
                                ref={el => (endElementRef.current[index] = el)}
                                className={styles.scrollToEnd}
                            >
                                {isLoading ? <LoadingOutlined /> : hasMore ? <>Load More</> : <>No more items</>}
                            </div>
                        </div>
                    ))}
                </div>
                <div
                    className={styles.scrollMap}

                >
                    <div className={styles.scrollBackground} ref={scrollMapRef}>
                        {visibleColumns.map((column) => (
                            <div
                                key={column.key}
                                className={styles.scrollMapColumn}
                            />
                        ))}
                    </div>
                    <div
                        ref={scrollIndicatorRef}
                        className={styles.scrollIndicator}
                        onMouseDown={handleDragStart}
                    />
                </div>
            </div>
        </div>
    )
}

KanabanBoard.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    renderItem: PropTypes.func,
    onItemClick: PropTypes.func,
    isLoading: PropTypes.bool,
    hasMore: PropTypes.bool,
    loadMore: PropTypes.func,
}

export default KanabanBoard
